/**
 * IANA time zone list for the event form. A free-text field let typos like
 * `Europe/Belgium` reach the database, where they crash the static site build
 * (`RangeError: Invalid time zone specified`), so the admin now picks from
 * the runtime's own list.
 */

/** Used only if the browser predates `Intl.supportedValuesOf` (pre-2022). */
const FALLBACK_TIMEZONES: readonly string[] = [
  'UTC',
  'Europe/Berlin',
  'Europe/Brussels',
  'Europe/Paris',
  'Europe/Amsterdam',
  'Europe/London',
  'Europe/Prague',
  'Europe/Warsaw',
  'Europe/Stockholm',
  'Europe/Helsinki',
  'Europe/Moscow',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Toronto',
  'America/Vancouver',
  'Asia/Ho_Chi_Minh',
  'Asia/Bangkok',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Asia/Seoul',
  'Australia/Sydney',
  'Australia/Melbourne',
  'Pacific/Auckland',
];

function loadTimezones(): readonly string[] {
  try {
    const zones = Intl.supportedValuesOf?.('timeZone');
    if (zones?.length) return ['UTC', ...zones.filter((z) => z !== 'UTC')];
  } catch {
    /* fall through */
  }
  return FALLBACK_TIMEZONES;
}

export const TIMEZONES = loadTimezones();

const TIMEZONE_SET = new Set(TIMEZONES);

/** True when `tz` is one of the zones offered by the picker. */
export function isKnownTimeZone(tz: string): boolean {
  return TIMEZONE_SET.has(tz);
}

/** "Europe/Brussels · GMT+02:00" — the offset confirms the right pick. */
export function timezoneLabel(tz: string): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'longOffset',
    }).formatToParts(new Date());
    const offset = parts.find((p) => p.type === 'timeZoneName')?.value;
    return offset ? `${tz} · ${offset}` : tz;
  } catch {
    return tz;
  }
}
